import { useState, useEffect } from 'react';
import { Search, Plus, Save, Eye, AlertCircle, Check, X, Image as ImageIcon } from 'lucide-react';
import { AdminLayout } from '../../components/admin/layout/AdminLayout';
import { Card } from '../../components/admin/ui/Card';
import { Button } from '../../components/admin/ui/Button';
import { Input } from '../../components/admin/ui/Input';
import { supabase } from '../../lib/supabase';

interface SeoPage {
  id: string;
  slug: string;
  title: string;
  meta_description: string;
  canonical_url: string | null;
  og_title: string | null;
  og_description: string | null;
  og_image: string | null;
  robots_directive: string;
  keywords: string | null;
  structured_data_json: any;
  is_active: boolean;
  updated_at: string;
}

export default function SeoManagementPage() {
  const [pages, setPages] = useState<SeoPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPage, setSelectedPage] = useState<SeoPage | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [formData, setFormData] = useState<Partial<SeoPage>>({});

  useEffect(() => {
    loadPages();
  }, []);

  async function loadPages() {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('seo_pages')
        .select('*')
        .order('slug', { ascending: true });

      if (error) throw error;
      setPages(data || []);
    } catch (error) {
      console.error('Error loading SEO pages:', error);
    } finally {
      setLoading(false);
    }
  }

  function handleEdit(page: SeoPage) {
    setSelectedPage(page);
    setFormData(page);
    setIsEditing(true);
    setSaveSuccess(false);
  }

  function handleCancel() {
    setSelectedPage(null);
    setFormData({});
    setIsEditing(false);
    setSaveSuccess(false);
  }

  async function handleSave() {
    if (!selectedPage) return;

    try {
      setSaving(true);
      const { error } = await supabase
        .from('seo_pages')
        .update({
          title: formData.title,
          meta_description: formData.meta_description,
          canonical_url: formData.canonical_url || null,
          og_title: formData.og_title || null,
          og_description: formData.og_description || null,
          og_image: formData.og_image || null,
          robots_directive: formData.robots_directive || 'index, follow',
          keywords: formData.keywords || null,
          is_active: formData.is_active ?? true
        })
        .eq('id', selectedPage.id);

      if (error) throw error;

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      await loadPages();

      const updatedPage = pages.find(p => p.id === selectedPage.id);
      if (updatedPage) {
        setSelectedPage({ ...updatedPage, ...formData } as SeoPage);
      }
    } catch (error) {
      console.error('Error saving SEO page:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  }

  const filteredPages = pages.filter(page =>
    page.slug.toLowerCase().includes(searchTerm.toLowerCase()) ||
    page.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getTitleLength = () => (formData.title || '').length;
  const getDescriptionLength = () => (formData.meta_description || '').length;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gestion SEO</h1>
            <p className="text-gray-600 mt-1">Gérez les métadonnées SEO des pages marketing</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <Card>
              <div className="p-4 border-b border-gray-200">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    placeholder="Rechercher une page..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="divide-y divide-gray-200 max-h-[calc(100vh-300px)] overflow-y-auto">
                {loading ? (
                  <div className="p-8 text-center text-gray-500">Chargement...</div>
                ) : filteredPages.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">Aucune page trouvée</div>
                ) : (
                  filteredPages.map((page) => (
                    <div
                      key={page.id}
                      onClick={() => handleEdit(page)}
                      className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                        selectedPage?.id === page.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">/{page.slug}</p>
                          <p className="text-xs text-gray-500 mt-1 truncate">{page.title}</p>
                        </div>
                        <div className="ml-2 flex-shrink-0">
                          {page.is_active ? (
                            <Check size={16} className="text-green-500" />
                          ) : (
                            <X size={16} className="text-gray-400" />
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </Card>
          </div>

          <div className="lg:col-span-2">
            {!isEditing ? (
              <Card>
                <div className="p-12 text-center text-gray-500">
                  <Eye size={48} className="mx-auto mb-4 text-gray-400" />
                  <p>Sélectionnez une page pour modifier ses métadonnées SEO</p>
                </div>
              </Card>
            ) : (
              <div className="space-y-6">
                <Card>
                  <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-lg font-semibold text-gray-900">/{formData.slug}</h2>
                        <p className="text-sm text-gray-500 mt-1">Dernière modification: {new Date(selectedPage?.updated_at || '').toLocaleDateString('fr-FR')}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={formData.is_active ?? true}
                            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-gray-700">Page active</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="p-6 space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Title SEO
                        <span className={`ml-2 text-xs ${getTitleLength() > 60 ? 'text-orange-600' : 'text-gray-500'}`}>
                          {getTitleLength()}/60 caractères {getTitleLength() > 60 && '(trop long)'}
                        </span>
                      </label>
                      <Input
                        value={formData.title || ''}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        placeholder="Titre qui apparaîtra dans les résultats Google"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Meta Description
                        <span className={`ml-2 text-xs ${getDescriptionLength() > 160 ? 'text-orange-600' : 'text-gray-500'}`}>
                          {getDescriptionLength()}/160 caractères {getDescriptionLength() > 160 && '(trop long)'}
                        </span>
                      </label>
                      <textarea
                        value={formData.meta_description || ''}
                        onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                        placeholder="Description qui apparaîtra dans les résultats Google"
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Keywords (séparés par des virgules)
                      </label>
                      <Input
                        value={formData.keywords || ''}
                        onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                        placeholder="mot-clé 1, mot-clé 2, mot-clé 3"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Canonical URL (optionnel)
                      </label>
                      <Input
                        value={formData.canonical_url || ''}
                        onChange={(e) => setFormData({ ...formData, canonical_url: e.target.value })}
                        placeholder="Laissez vide pour auto-génération"
                      />
                      <p className="text-xs text-gray-500 mt-1">Si vide, sera auto-généré vers l'URL actuelle</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Directive Robots
                      </label>
                      <select
                        value={formData.robots_directive || 'index, follow'}
                        onChange={(e) => setFormData({ ...formData, robots_directive: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="index, follow">index, follow (recommandé)</option>
                        <option value="noindex, follow">noindex, follow</option>
                        <option value="index, nofollow">index, nofollow</option>
                        <option value="noindex, nofollow">noindex, nofollow</option>
                      </select>
                    </div>
                  </div>
                </Card>

                <Card>
                  <div className="p-6 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">Open Graph (Réseaux Sociaux)</h3>
                  </div>

                  <div className="p-6 space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        OG Title
                      </label>
                      <Input
                        value={formData.og_title || ''}
                        onChange={(e) => setFormData({ ...formData, og_title: e.target.value })}
                        placeholder="Titre pour Facebook, Twitter, LinkedIn..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        OG Description
                      </label>
                      <textarea
                        value={formData.og_description || ''}
                        onChange={(e) => setFormData({ ...formData, og_description: e.target.value })}
                        placeholder="Description pour les réseaux sociaux"
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        OG Image URL
                      </label>
                      <Input
                        value={formData.og_image || ''}
                        onChange={(e) => setFormData({ ...formData, og_image: e.target.value })}
                        placeholder="https://trezoone.fr/image.jpg"
                      />
                      <p className="text-xs text-gray-500 mt-1">Recommandé: 1200x630px</p>
                    </div>
                  </div>
                </Card>

                <Card>
                  <div className="p-6 bg-blue-50 border-l-4 border-l-blue-500">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
                      <div className="text-sm text-blue-900">
                        <p className="font-semibold mb-1">Aperçu SERP Google</p>
                        <div className="mt-3 bg-white p-4 rounded border border-blue-200">
                          <div className="text-blue-700 text-lg hover:underline cursor-pointer">{formData.title || 'Titre de la page'}</div>
                          <div className="text-green-700 text-xs mt-1">https://trezoone.fr/{formData.slug}</div>
                          <div className="text-gray-600 text-sm mt-2">{formData.meta_description || 'Description de la page...'}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>

                <div className="flex items-center gap-3">
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    variant="primary"
                    className="flex-1"
                  >
                    <Save size={18} />
                    {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
                  </Button>
                  <Button
                    onClick={handleCancel}
                    variant="outline"
                  >
                    Annuler
                  </Button>
                </div>

                {saveSuccess && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-800">
                    <Check size={18} />
                    <span>Modifications enregistrées avec succès</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
